import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/Button';
import { EsosaProvider } from '../components/AIAssistant/EsosaContext';
import EsosaAssistant from '../components/AIAssistant/EsosaAssistant';
import { CheckCircleIcon, ArrowRightIcon, ShieldCheckIcon, ClockIcon, CreditCardIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, UserIcon, FileTextIcon, BookOpenIcon, BuildingIcon, LeafIcon, LockIcon, BarChartIcon, FileIcon, SparklesIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { mdas } from '../data/mdas';
const carouselImages = [{
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbMjzGrUbajiGhXQhqaeZE/1000643730.jpg',
  alt: 'Ministry of Health – health workers attending to patients',
  ministry: 'Ministry of Health'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oUhbwEBCgnRvKJzUE8pR7M/1000643732.jpg',
  alt: 'Ministry of Finance – digital tax collection system',
  ministry: 'Ministry of Finance'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/f6hBbKi8YiapLccZj8h2Tk/1000643733.jpg',
  alt: 'Ministry of Transportation – smart traffic system',
  ministry: 'Ministry of Transportation'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2LCfLvkuA42J5Tm7FeE3i3/1000643735.jpg',
  alt: 'Ministry of Education – digital classroom tools',
  ministry: 'Ministry of Education'
}, {
  src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aqv5qA57GoPz2gpGezq9jH/1000643736.jpg',
  alt: 'Ministry of Agriculture – e-extension services to farmers',
  ministry: 'Ministry of Agriculture'
}];
const quickServices = [{
  icon: <UserIcon className="h-6 w-6" data-id="element-5371" />,
  title: 'Book a Hospital Appointment',
  description: 'Schedule medical consultations easily',
  color: 'blue'
}, {
  icon: <CreditCardIcon className="h-6 w-6" data-id="element-5372" />,
  title: "Renew Driver's License",
  description: 'Quick and hassle-free renewal process',
  color: 'purple'
}, {
  icon: <FileTextIcon className="h-6 w-6" data-id="element-5373" />,
  title: 'Pay Your Taxes',
  description: 'Convenient tax payment solutions',
  color: 'green'
}, {
  icon: <BuildingIcon className="h-6 w-6" data-id="element-5374" />,
  title: 'Access Court Records',
  description: 'Retrieve legal documents securely',
  color: 'orange'
}, {
  icon: <BookOpenIcon className="h-6 w-6" data-id="element-5375" />,
  title: 'Apply for Scholarships',
  description: 'Educational funding opportunities',
  color: 'indigo'
}, {
  icon: <LeafIcon className="h-6 w-6" data-id="element-5376" />,
  title: 'Get Agro Inputs Support',
  description: 'Agricultural resources and assistance',
  color: 'emerald'
}];
const stats = [{
  label: 'Active Users',
  value: '500K+',
  icon: UsersIcon
}, {
  label: 'Services Available',
  value: '100+',
  icon: SparklesIcon
}, {
  label: 'Applications Processed',
  value: '2M+',
  icon: TrendingUpIcon
}, {
  label: 'Customer Satisfaction',
  value: '98%',
  icon: CheckCircleIcon
}];
const LandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => prev === carouselImages.length - 1 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  const nextSlide = () => {
    setCurrentSlide(prev => prev === carouselImages.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? carouselImages.length - 1 : prev - 1);
  };
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    indigo: 'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600'
  };
  return <EsosaProvider data-id="element-5377">
      <div className="min-h-screen flex flex-col bg-white" data-id="element-5378">
        <Header data-id="element-5379" />

        {/* Hero Carousel Section */}
        <section className="relative h-[600px] md:h-[650px] overflow-hidden" data-id="element-5380">
          {/* Carousel Images */}
          {carouselImages.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-5381">
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60 z-10" data-id="element-5382"></div>
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover" data-id="element-5383" />
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm text-green-800 px-4 py-2 rounded-full text-sm font-medium z-20 shadow-lg" data-id="element-5384">
                {image.ministry}
              </div>
            </div>)}

          {/* Carousel Controls */}
          <button onClick={prevSlide} className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full p-3 z-20 text-white transition-all duration-300 hover:scale-110" aria-label="Previous slide" data-id="element-5385">
            <ChevronLeftIcon className="h-6 w-6" data-id="element-5386" />
          </button>
          <button onClick={nextSlide} className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full p-3 z-20 text-white transition-all duration-300 hover:scale-110" aria-label="Next slide" data-id="element-5387">
            <ChevronRightIcon className="h-6 w-6" data-id="element-5388" />
          </button>

          {/* Carousel Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20" data-id="element-5389">
            {carouselImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`transition-all duration-300 rounded-full ${index === currentSlide ? 'w-8 h-3 bg-white' : 'w-3 h-3 bg-white/50 hover:bg-white/70'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-5390"></button>)}
          </div>

          {/* Hero Content */}
          <div className="absolute inset-0 flex items-center justify-center z-20" data-id="element-5391">
            <div className="text-center text-white px-4 max-w-4xl" data-id="element-5392">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fade-in" data-id="element-5393">
                Seamless Government Services. One Platform.
              </h1>
              <p className="text-lg md:text-xl text-white/95 mb-8 max-w-3xl mx-auto leading-relaxed" data-id="element-5394">
                Access trusted, digital public services across all MDAs in Edo
                State—fast, transparent, and citizen-focused.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-5395">
                <Link to="/login" data-id="element-5396">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105" data-id="element-5397">
                    Get Started
                    <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-5398" />
                  </Button>
                </Link>
                <Link to="/mdas" data-id="element-5399">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border-2 border-white hover:bg-white hover:text-green-800 transition-all duration-300 hover:scale-105" data-id="element-5400">
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gradient-to-r from-green-700 to-green-800 py-12" data-id="element-5401">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5402">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-5403">
              {stats.map((stat, index) => <div key={index} className="text-center transform transition-all duration-300 hover:scale-105" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }} data-id="element-5404">
                  <div className="flex justify-center mb-3" data-id="element-5405">
                    <div className="p-3 bg-white/10 rounded-full" data-id="element-5406">
                      <stat.icon className="h-8 w-8 text-white" data-id="element-5407" />
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-5408">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-green-100" data-id="element-5409">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Quick Services Grid Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-id="element-5410">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5411">
            <div className="text-center mb-16" data-id="element-5412">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-5413">
                Popular Services at Your Fingertips
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-5414">
                Access these essential government services quickly and
                efficiently
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-5415">
              {quickServices.map((service, index) => <Link key={index} to="/mdas" className="group" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }} data-id="element-5416">
                  <div className="relative bg-white rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl border border-gray-100 hover:border-transparent overflow-hidden h-full" data-id="element-5417">
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[service.color as keyof typeof colorClasses]} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} data-id="element-5418"></div>

                    <div className="relative z-10" data-id="element-5419">
                      <div className={`w-14 h-14 bg-gradient-to-br ${colorClasses[service.color as keyof typeof colorClasses]} rounded-xl flex items-center justify-center mb-5 text-white transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg`} data-id="element-5420">
                        {service.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-700 transition-colors" data-id="element-5421">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-5 leading-relaxed" data-id="element-5422">
                        {service.description}
                      </p>
                      <div className="flex items-center text-green-600 font-medium group-hover:text-green-700 transition-colors" data-id="element-5423">
                        <span data-id="element-5424">Access Service</span>
                        <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-2 transition-transform duration-300" data-id="element-5425" />
                      </div>
                    </div>
                  </div>
                </Link>)}
            </div>
            <div className="text-center mt-12" data-id="element-5426">
              <Link to="/mdas" data-id="element-5427">
                <Button variant="outline" className="inline-flex items-center hover:bg-green-50 hover:border-green-600 hover:text-green-700 transition-all duration-300" data-id="element-5428">
                  View All Services
                  <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-5429" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white" data-id="element-5430">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5431">
            <div className="text-center mb-16" data-id="element-5432">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-5433">
                How It Works
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-5434">
                Complete government transactions in just a few simple steps
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-5435">
              {/* Connection lines for desktop */}
              <div className="hidden md:block absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-green-200 via-green-300 to-green-200" data-id="element-5436"></div>

              {[{
              step: 1,
              title: 'Sign In or Register',
              description: 'Securely log in using your government-issued credentials or create a new account in seconds to access your personalized dashboard.'
            }, {
              step: 2,
              title: 'Choose a Service',
              description: 'Browse and select from 100+ verified services across all government departments — from permits to payments, all in one place.'
            }, {
              step: 3,
              title: 'Get Real-Time Results',
              description: 'Track applications instantly, receive timely updates, and download electronic receipts — all with a seamless digital experience.'
            }].map((item, index) => <div key={index} className="relative" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.2}s both`
            }} data-id="element-5437">
                  <div className="flex flex-col items-center text-center" data-id="element-5438">
                    <div className="relative z-10 w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 text-white rounded-2xl flex items-center justify-center mb-6 text-2xl font-bold shadow-xl transform transition-all duration-300 hover:scale-110 hover:rotate-3" data-id="element-5439">
                      {item.step}
                      <div className="absolute -inset-1 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl blur opacity-30" data-id="element-5440"></div>
                    </div>
                    <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4" data-id="element-5441">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-5442">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Trust & Security Section */}
        <section className="py-20 bg-gradient-to-br from-green-800 via-green-700 to-green-900 text-white relative overflow-hidden" data-id="element-5443">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10" data-id="element-5444">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" data-id="element-5445"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-5446"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-5447">
            <div className="text-center mb-16" data-id="element-5448">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5449">
                Built on Trust. Secured by Technology.
              </h2>
              <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto" data-id="element-5450">
                Your data and transactions are protected by enterprise-grade
                security
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-5451">
              {[{
              icon: LockIcon,
              title: '256-bit SSL Security',
              description: 'Bank-grade encryption for all your sensitive data'
            }, {
              icon: ShieldCheckIcon,
              title: 'Verified by Edo ICT Agency',
              description: 'All services are officially approved and monitored'
            }, {
              icon: FileIcon,
              title: 'Audit Trails',
              description: 'Complete transparency with detailed transaction records'
            }, {
              icon: BarChartIcon,
              title: '500,000+ Citizens Served',
              description: 'Trusted by residents across Edo State'
            }].map((item, index) => <div key={index} className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl text-center transform transition-all duration-300 hover:bg-white/15 hover:scale-105 border border-white/20" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }} data-id="element-5452">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-5453">
                    <item.icon className="h-8 w-8 text-white" data-id="element-5454" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3" data-id="element-5455">{item.title}</h3>
                  <p className="text-green-100 text-sm leading-relaxed" data-id="element-5456">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50" data-id="element-5457">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5458">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 md:p-16 shadow-2xl border border-green-200 relative overflow-hidden" data-id="element-5459">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-green-200 rounded-full blur-3xl opacity-30 -mr-32 -mt-32" data-id="element-5460"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-300 rounded-full blur-3xl opacity-30 -ml-32 -mb-32" data-id="element-5461"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center" data-id="element-5462">
                <div className="md:w-2/3 mb-8 md:mb-0 md:pr-12" data-id="element-5463">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-5464">
                    Ready to Access Government Services?
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-5465">
                    Create an account today and experience the convenience of
                    digital government services. Join thousands of satisfied
                    citizens.
                  </p>
                  <div className="flex flex-wrap gap-4" data-id="element-5466">
                    <Link to="/register" data-id="element-5467">
                      <Button variant="primary" size="lg" className="shadow-xl hover:shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105" data-id="element-5468">
                        Create an Account
                        <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-5469" />
                      </Button>
                    </Link>
                    <Link to="/login" data-id="element-5470">
                      <Button variant="outline" size="lg" className="bg-white hover:bg-gray-50 transition-all duration-300" data-id="element-5471">
                        Login
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="md:w-1/3 flex justify-center" data-id="element-5472">
                  <div className="relative" data-id="element-5473">
                    <div className="absolute inset-0 bg-green-400 rounded-full blur-2xl opacity-20" data-id="element-5474"></div>
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Government Seal" className="h-40 md:h-56 relative z-10 transform transition-transform duration-300 hover:scale-110" data-id="element-5475" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partner MDAs Section */}
        <section className="py-20 bg-white" data-id="element-5476">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5477">
            <div className="text-center mb-16" data-id="element-5478">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-5479">
                Partner Ministries, Departments & Agencies
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-5480">
                Working together to deliver efficient government services
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8" data-id="element-5481">
              {mdas.slice(0, 6).map((mda, index) => <div key={mda.id} className="flex flex-col items-center p-6 bg-gray-50 rounded-2xl transition-all duration-300 hover:bg-white hover:shadow-xl border border-transparent hover:border-green-100 group" style={{
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }} data-id="element-5482">
                  <div className="w-20 h-20 mb-4 flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110" data-id="element-5483">
                    <img src={mda.logoUrl} alt={`${mda.name} Logo`} className="h-full w-full object-contain" data-id="element-5484" />
                  </div>
                  <span className="text-sm font-medium text-center text-gray-700 group-hover:text-green-700 transition-colors" data-id="element-5485">
                    {mda.acronym}
                  </span>
                </div>)}
            </div>
            <div className="text-center mt-12" data-id="element-5486">
              <Link to="/mdas" data-id="element-5487">
                <Button variant="outline" className="inline-flex items-center hover:bg-green-50 hover:border-green-600 hover:text-green-700 transition-all duration-300" data-id="element-5488">
                  View All MDAs
                  <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-5489" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Footer data-id="element-5490" />
        <EsosaAssistant data-id="element-5491" />
      </div>

      <style data-id="element-5492">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </EsosaProvider>;
};
export default LandingPage;